import { useTheme } from '@retailer-platform/shared-components/src/tds'
import {
  CartIcon,
  CartOutlineIcon,
  DealsCategoryFilledIcon,
  DealsCategoryIcon,
} from '@instacart/ids-core'
import { tdsNavIcon, tdsNavIconFromIds } from './tdsNavIcon'
import { cloneDeep, sortBy } from 'lodash-es'
import { useMemo } from 'react'
import usePartnerAccessControl from '../../../legacy/components/AccessControl/usePartnerAccessControl'
import { NoticeBannersAccessControl } from '../../../sections/admin/notice-banners/access-control'
import { type RPPNavigationEntry } from '../../../utils/core/RPPCore.types'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import {
  type NavMenuHierarchyL1,
  type NavMenuHierarchyL2,
  clearEmptyBranches,
  indexRoutes,
  removeInsufficientAccessNavEntries,
} from '../nav/utils/NavMenuHierarchy'
import { AdminNavEntryPointV3 } from './adminNavEntryPointsV3'
import { ADMIN_NAV_SECTION_TITLES, AdminNavSectionV3 } from './adminNavSectionV3'
import { type NavMenuHierarchyV3 } from './navSectionV3'

// v3 admin sidebar IA — admin Figma node 10972:23798. 8 L1s grouped under four
// section headers (Global, Commerce, Operations, Admin). Mirrors the retailer
// v3 hook (`useNavHierarchyRetailerDataV3.tsx`) but reads from the admin
// `domainAdminNavigationEntriesV3` context map and has no tier gating.
//
// Icons use the TDS NavigationBar icon set (`NavigationBar.Icon name=...`).
const useNavHierarchyAdminDataV3Static = (): NavMenuHierarchyV3 => {
  const theme = useTheme()

  return useMemo(() => {
    const home: NavMenuHierarchyL1 = {
      name: 'navV3.folder.home.title',
      icon: tdsNavIcon('home'),
      // Non-expandable L1: clicking navigates to the admin landing route
      // instead of toggling a NavGroupContent panel. Mirrors `homeRoute` below.
      route: 'app-admin',
      children: [],
    }
    const performance: NavMenuHierarchyL1 = {
      name: 'navV3.folder.performance.title',
      icon: tdsNavIcon('analytics'),
      navEntryPointV3: AdminNavEntryPointV3.Performance,
      children: [],
    }
    const catalog: NavMenuHierarchyL1 = {
      name: 'navV2.folder.catalog.title',
      icon: tdsNavIcon('catalog'),
      navEntryPointV3: AdminNavEntryPointV3.Catalog,
      children: [],
    }
    const marketplace: NavMenuHierarchyL1 = {
      name: 'navV3.folder.marketplace.title',
      icon: tdsNavIcon('marketplace'),
      navEntryPointV3: AdminNavEntryPointV3.Marketplace,
      children: [],
    }
    const storefrontPro: NavMenuHierarchyL1 = {
      name: 'navV3.folder.storefrontPro.title',
      icon: tdsNavIcon('storefrontPro'),
      navEntryPointV3: AdminNavEntryPointV3.StorefrontPro,
      children: [],
    }
    const customersAndOrders: NavMenuHierarchyL1 = {
      name: 'navV3.folder.customersAndOrders.title',
      icon: tdsNavIcon('orders'),
      navEntryPointV3: AdminNavEntryPointV3.CustomersAndOrders,
      children: [],
    }
    const connectedStores: NavMenuHierarchyL1 = {
      name: 'navV3.folder.connectedStores.title',
      // Cart glyph (ids-core) — TDS NavigationBar's `stores` is a storefront,
      // which doesn't match the design.
      icon: tdsNavIconFromIds(CartIcon, CartOutlineIcon),
      navEntryPointV3: AdminNavEntryPointV3.ConnectedStores,
      children: [],
    }
    const fulfillment: NavMenuHierarchyL1 = {
      name: 'navV3.folder.fulfillment.title',
      icon: tdsNavIcon('fulfillment'),
      navEntryPointV3: AdminNavEntryPointV3.Fulfillment,
      children: [],
    }
    const financialIntegrity: NavMenuHierarchyL1 = {
      name: 'navV3.folder.financialIntegrity.title',
      // Deals/price-tag glyph (ids-core) — TDS NavigationBar has no matching
      // glyph and `merchandising` is a tag-with-percent that doesn't match the
      // design (Figma uses the plain price tag).
      icon: tdsNavIconFromIds(DealsCategoryFilledIcon, DealsCategoryIcon),
      navEntryPointV3: AdminNavEntryPointV3.FinancialIntegrity,
      children: [],
    }
    const partnerSettings: NavMenuHierarchyL1 = {
      name: 'navV3.folder.partnerSettings.title',
      icon: tdsNavIcon('settings'),
      navEntryPointV3: AdminNavEntryPointV3.PartnerSettings,
      // Static L2 for Notice banners — this admin tool lives in
      // packages/dashboard/src/sections/admin/notice-banners (not a domain),
      // so it can't register via `adminNavigationEntries` on a domain
      // definition like the sibling entries do. `attachDomainsV3` concats
      // domain-contributed children onto whatever's pre-populated here and
      // sorts by `positionNavV3`, so the entry lands at the tail of group 3
      // (after Custom strings at position 9).
      children: [
        {
          name: 'navV3.folder.partnerSettings.noticeBanners',
          route: 'admin-notice-banners',
          accessControl: NoticeBannersAccessControl,
          positionNavV3: 10,
        },
      ],
    }

    return {
      navBarColor: theme.colors.systemGrayscale80,
      navBarColorHover: theme.colors.systemGrayscale60,
      type: 'admin',
      homeRoute: 'app-admin',
      sections: [
        // Global has no titleId → renderer suppresses its header, matching v2
        // admin's "no section headers" layout for the unsectioned top group.
        { id: AdminNavSectionV3.Global, l1s: [home, performance] },
        {
          id: AdminNavSectionV3.Commerce,
          titleId: ADMIN_NAV_SECTION_TITLES[AdminNavSectionV3.Commerce],
          l1s: [catalog, marketplace, storefrontPro],
        },
        {
          id: AdminNavSectionV3.Operations,
          titleId: ADMIN_NAV_SECTION_TITLES[AdminNavSectionV3.Operations],
          l1s: [customersAndOrders, connectedStores, fulfillment],
        },
        {
          id: AdminNavSectionV3.Admin,
          titleId: ADMIN_NAV_SECTION_TITLES[AdminNavSectionV3.Admin],
          l1s: [financialIntegrity, partnerSettings],
        },
      ],
    } satisfies NavMenuHierarchyV3
  }, [theme.colors.systemGrayscale80, theme.colors.systemGrayscale60])
}

// Mirrors `attachDomainsV3` in the retailer hook: walks the section tree and
// merges domain-registered entries into each L1 by `navEntryPointV3`. Kept as
// a local copy (rather than shared) to match the retailer hook's structure
// exactly.
const attachDomainsV3 = (
  currentNode: NavMenuHierarchyL1 | NavMenuHierarchyL2,
  entriesByAttachToV3: Partial<Record<AdminNavEntryPointV3, RPPNavigationEntry[]>>
) => {
  currentNode.children?.forEach(child => {
    attachDomainsV3(child, entriesByAttachToV3)
  })

  if (currentNode.navEntryPointV3) {
    const ownEntries =
      entriesByAttachToV3[currentNode.navEntryPointV3 as AdminNavEntryPointV3] ?? []
    const newChildren: NavMenuHierarchyL2[] = ownEntries.map(e => ({
      ...e,
      name: e.labelIdV3 ?? e.labelId,
    }))
    currentNode.children = sortBy(
      (currentNode.children || []).concat(newChildren),
      child => child.positionNavV3 ?? 0
    )
  }
}

export const useNavHierarchyAdminV3 = (doAccessCheck: boolean = true) => {
  const hasAccess = usePartnerAccessControl()
  const { domainAdminNavigationEntriesV3 = {} } = useCoreContext()
  const navMenuHierarchy = useNavHierarchyAdminDataV3Static()

  return useMemo(() => {
    const indexedL1ByRoutes: { [key: string]: NavMenuHierarchyL1 } = {}
    const clonedHierarchy = cloneDeep(navMenuHierarchy)

    clonedHierarchy.sections.forEach(section => {
      section.l1s.forEach(l1 => {
        attachDomainsV3(l1, domainAdminNavigationEntriesV3)
        if (doAccessCheck) {
          removeInsufficientAccessNavEntries(l1, hasAccess)
        }
        clearEmptyBranches(l1)
        indexRoutes(l1, l1, indexedL1ByRoutes)
      })
      // Cull L1s with no surviving children that aren't themselves leaf
      // navigation targets. Admin has no leaf L1s today, but the check matches
      // the retailer hook so it stays robust if leaf admin L1s appear later.
      section.l1s = section.l1s.filter(
        l1 => l1.children.length > 0 || Boolean(l1.route) || Boolean(l1.href)
      )
    })

    clonedHierarchy.sections = clonedHierarchy.sections.filter(s => s.l1s.length > 0)

    return { navMenuHierarchy: clonedHierarchy, indexedL1ByRoutes }
  }, [navMenuHierarchy, domainAdminNavigationEntriesV3, hasAccess, doAccessCheck])
}
